import React, { useState, useEffect } from "react";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Chip
} from "@material-ui/core";
import {
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(2),
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  addButton: {
    marginBottom: theme.spacing(2),
  },
}));

// value vai para o backend; o rótulo é traduzido no render.
const fieldTypeOptions = [
  { value: "text", labelKey: "clienteCustomFieldsManager.types.text" },
  { value: "number", labelKey: "clienteCustomFieldsManager.types.number" },
  { value: "date", labelKey: "clienteCustomFieldsManager.types.date" },
  { value: "select", labelKey: "clienteCustomFieldsManager.types.select" },
  { value: "multiselect", labelKey: "clienteCustomFieldsManager.types.multiselect" },
  { value: "checkbox", labelKey: "clienteCustomFieldsManager.types.checkbox" },
  { value: "textarea", labelKey: "clienteCustomFieldsManager.types.textarea" },
  { value: "email", labelKey: "clienteCustomFieldsManager.types.email" },
  { value: "phone", labelKey: "clienteCustomFieldsManager.types.phone" },
  { value: "cpf", labelKey: "clienteCustomFieldsManager.types.cpf" },
  { value: "cnpj", labelKey: "clienteCustomFieldsManager.types.cnpj" },
  { value: "cep", labelKey: "clienteCustomFieldsManager.types.cep" }
];

const reportableFieldTypes = ["select", "checkbox", "multiselect"];

const ClienteCustomFieldsManager = () => {
  const classes = useStyles();
  const [definitions, setDefinitions] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [formData, setFormData] = useState({
    fieldName: "",
    fieldLabel: "",
    fieldType: "text",
    fieldOptions: "",
    required: false,
    includeInReport: false,
    orderIndex: 0,
    defaultValue: ""
  });

  useEffect(() => {
    loadDefinitions();
  }, []);

  const loadDefinitions = async () => {
    try {
      const { data } = await api.get("/cliente-custom-field-definitions");
      
      // Garantir que sempre seja um array
      const definitions = Array.isArray(data?.definitions) 
        ? data.definitions 
        : Array.isArray(data) 
        ? data 
        : [];
      
      setDefinitions(definitions);
    } catch (err) {
      toast.error(i18n.t("clienteCustomFieldsManager.toasts.loadError"));
      setDefinitions([]); // Garantir que seja um array vazio em caso de erro
    }
  };

  const handleOpenDialog = (definition = null) => {
    if (definition) {
      setSelectedDefinition(definition);
      setFormData({
        fieldName: definition.fieldName,
        fieldLabel: definition.fieldLabel,
        fieldType: definition.fieldType,
        fieldOptions: definition.fieldOptions 
          ? (Array.isArray(definition.fieldOptions) 
              ? definition.fieldOptions.join(", ") 
              : definition.fieldOptions)
          : "",
        required: definition.required,
        includeInReport: Boolean(definition.includeInReport),
        orderIndex: definition.orderIndex,
        defaultValue: definition.defaultValue || ""
      });
    } else {
      setSelectedDefinition(null);
      setFormData({
        fieldName: "",
        fieldLabel: "",
        fieldType: "text",
        fieldOptions: "",
        required: false,
        includeInReport: false,
        orderIndex: definitions.length,
        defaultValue: ""
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedDefinition(null);
    setFormData({
      fieldName: "",
      fieldLabel: "",
      fieldType: "text",
      fieldOptions: "",
      required: false,
      includeInReport: false,
      orderIndex: 0,
      defaultValue: ""
    });
  };

  const handleSave = async () => {
    try {
      // Validações
      if (!formData.fieldName || !formData.fieldLabel) {
        toast.error(i18n.t("clienteCustomFieldsManager.toasts.nameAndLabelRequired"));
        return;
      }

      // Converter nome do campo para formato camelCase
      const fieldName = formData.fieldName
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]/g, "_")
        .toLowerCase();

      // Preparar opções para select/multiselect
      let fieldOptions = null;
      if (["select", "multiselect"].includes(formData.fieldType)) {
        if (formData.fieldOptions) {
          fieldOptions = formData.fieldOptions
            .split(",")
            .map(opt => opt.trim())
            .filter(opt => opt);
        }
      }

      const payload = {
        ...formData,
        fieldName,
        includeInReport: reportableFieldTypes.includes(formData.fieldType)
          ? formData.includeInReport
          : false,
        fieldOptions
      };

      if (selectedDefinition) {
        await api.put(`/cliente-custom-field-definitions/${selectedDefinition.id}`, payload);
        toast.success(i18n.t("clienteCustomFieldsManager.toasts.updated"));
      } else {
        await api.post("/cliente-custom-field-definitions", payload);
        toast.success(i18n.t("clienteCustomFieldsManager.toasts.created"));
      }

      loadDefinitions();
      handleCloseDialog();
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("clienteCustomFieldsManager.toasts.saveError"));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(i18n.t("clienteCustomFieldsManager.confirmDelete"))) {
      try {
        await api.delete(`/cliente-custom-field-definitions/${id}`);
        toast.success(i18n.t("clienteCustomFieldsManager.toasts.deleted"));
        loadDefinitions();
      } catch (err) {
        toast.error(i18n.t("clienteCustomFieldsManager.toasts.deleteError"));
      }
    }
  };

  const getFieldTypeLabel = (type) => {
    const option = fieldTypeOptions.find(opt => opt.value === type);
    return option ? i18n.t(option.labelKey) : type;
  };

  const canIncludeInReport = reportableFieldTypes.includes(formData.fieldType);

  return (
    <Paper className={classes.mainPaper} variant="outlined">
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        className={classes.addButton}
        onClick={() => handleOpenDialog()}
      >
        {i18n.t("clienteCustomFieldsManager.addButton")}
      </Button>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{i18n.t("clienteCustomFieldsManager.table.order")}</TableCell>
            <TableCell>{i18n.t("clienteCustomFieldsManager.table.label")}</TableCell>
            <TableCell>{i18n.t("clienteCustomFieldsManager.table.fieldName")}</TableCell>
            <TableCell>{i18n.t("common.type")}</TableCell>
            <TableCell>{i18n.t("common.required")}</TableCell>
            <TableCell>{i18n.t("clienteCustomFieldsManager.table.inReport")}</TableCell>
            <TableCell align="center">{i18n.t("common.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {definitions.map((definition) => (
            <TableRow key={definition.id}>
              <TableCell>{definition.orderIndex}</TableCell>
              <TableCell>{definition.fieldLabel}</TableCell>
              <TableCell>{definition.fieldName}</TableCell>
              <TableCell>
                <Chip 
                  label={getFieldTypeLabel(definition.fieldType)} 
                  size="small" 
                  color="primary"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{definition.required ? i18n.t("common.yes") : i18n.t("common.no")}</TableCell>
              <TableCell>{definition.includeInReport ? i18n.t("common.yes") : i18n.t("common.no")}</TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  onClick={() => handleOpenDialog(definition)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(definition.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedDefinition
            ? i18n.t("clienteCustomFieldsManager.dialog.editTitle")
            : i18n.t("clienteCustomFieldsManager.dialog.newTitle")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={i18n.t("clienteCustomFieldsManager.dialog.fieldLabel")}
                fullWidth
                value={formData.fieldLabel}
                onChange={(e) => setFormData({ ...formData, fieldLabel: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={i18n.t("clienteCustomFieldsManager.dialog.fieldName")}
                fullWidth
                value={formData.fieldName}
                onChange={(e) => setFormData({ ...formData, fieldName: e.target.value })}
                margin="normal"
                required
                helperText={i18n.t("clienteCustomFieldsManager.dialog.fieldNameHelper")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={i18n.t("clienteCustomFieldsManager.dialog.fieldType")}
                fullWidth
                value={formData.fieldType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  const supportsReport = reportableFieldTypes.includes(nextType);

                  setFormData({
                    ...formData,
                    fieldType: nextType,
                    includeInReport: supportsReport ? formData.includeInReport : false
                  });
                }}
                margin="normal"
              >
                {fieldTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {i18n.t(option.labelKey)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={i18n.t("clienteCustomFieldsManager.dialog.orderIndex")}
                type="number"
                fullWidth
                value={formData.orderIndex}
                onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) })}
                margin="normal"
              />
            </Grid>
            {["select", "multiselect"].includes(formData.fieldType) && (
              <Grid item xs={12}>
                <TextField
                  label={i18n.t("clienteCustomFieldsManager.dialog.options")}
                  fullWidth
                  value={formData.fieldOptions}
                  onChange={(e) => setFormData({ ...formData, fieldOptions: e.target.value })}
                  margin="normal"
                  helperText={i18n.t("clienteCustomFieldsManager.dialog.optionsHelper")}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label={i18n.t("clienteCustomFieldsManager.dialog.defaultValue")}
                fullWidth
                value={formData.defaultValue}
                onChange={(e) => setFormData({ ...formData, defaultValue: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.required}
                    onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                    color="primary"
                  />
                }
                label={i18n.t("clienteCustomFieldsManager.dialog.required")}
              />
            </Grid>
            {canIncludeInReport && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.includeInReport}
                      onChange={(e) => setFormData({ ...formData, includeInReport: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={i18n.t("clienteCustomFieldsManager.dialog.includeInReport")}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{i18n.t("common.cancel")}</Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            {i18n.t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ClienteCustomFieldsManager;
